import { t } from '@lingui/macro';
import { type ApiClient, RefKnowledges, RefSkills } from '@prismo-io/schemas';
import { z } from 'zod';
import { SkillTypeEnum } from './skill-type-enum';

const DEFAULT_VALUE = -1;

const skillSchema = z.object({
  code: RefSkills.shape.code,
  referential_id: RefSkills.shape.referential_id,
});

const knowledgeSchema = z.object({
  code: RefKnowledges.shape.code,
  referential_id: RefKnowledges.shape.referential_id,
});

const withSkillKnowledgeSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal(SkillTypeEnum.Enum.SKILLS),
    })
    .merge(skillSchema),
  z
    .object({
      type: z.literal(SkillTypeEnum.Enum.KNOWLEDGE),
    })
    .merge(knowledgeSchema),
]);

const levelSchema = z.object({
  hasLevel: z.boolean(),
  level: z.number().nullable(),
});
const interestSchema = z.object({
  hasInterest: z.boolean(),
  interest: z.number().nullable(),
});

const ParamsSchema = z.object({
  skill: withSkillKnowledgeSchema,
  level: levelSchema,
  interest: interestSchema,
});

export type ParamsSchemaT = z.infer<typeof ParamsSchema>;

const ResultSchema = z.boolean();

export type ResultSchemaT = z.infer<typeof ResultSchema>;

export const sendSkill = async (
  client: ApiClient,
  params: ParamsSchemaT
): Promise<ResultSchemaT> => {
  const { skill, level, interest } = ParamsSchema.parse(params);

  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session) {
    throw new Error(t`Vous devez être connecté.`);
  }
  const userId = session.user.id;

  switch (skill.type) {
    case 'SKILLS':
      await client
        .from('quark_users_skills')
        .upsert(
          {
            user_id: userId,
            skill_referential_id: skill.referential_id,
            skill_code: skill.code,
            is_known: level.hasLevel,
            is_wish: interest.hasInterest,
            level: level.level ?? DEFAULT_VALUE,
            interest: interest.interest ?? DEFAULT_VALUE,
          },
          { onConflict: 'user_id, skill_code, skill_referential_id' }
        )
        .throwOnError();

      break;

    case 'KNOWLEDGE':
      await client
        .from('quark_users_knowledges')
        .upsert(
          {
            user_id: userId,
            knowledge_referential_id: skill.referential_id,
            knowledge_code: skill.code,
            is_known: level.hasLevel,
            is_wish: interest.hasInterest,
            level: level.level ?? DEFAULT_VALUE,
            interest: interest.interest ?? DEFAULT_VALUE,
          },
          { onConflict: 'user_id, knowledge_code, knowledge_referential_id' }
        )
        .select()
        .throwOnError();

      break;

    default:
      throw new Error(t`Le type ne correspond à aucun skill`);
      break;
  }

  return ResultSchema.parse(true);
};
